import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useMemo, useReducer, useState } from 'react'
import { useParams } from 'react-router-dom'
import _ from 'lodash'
import { RADOM_COLORS } from '../../util/Constants'
import { errorToast, infoToast, successToast } from '../../util/Util'
import Radom, { IWebhook, IWebhookMessage, WebhookMessagesStatus } from '../../state/Radom'
import Spinner from '../../components/Spinner'
import Table from '../../components/Table'
import Pagination from '../../components/Pagination'
import PageHeader from '../../components/PageHeader'
import TimeText from '../../components/TimeText'
import Modal from '../../components/Modal'
import ConfirmButtonModal from '../../components/ConfirmButtonModal'
import { IconButton } from '../../components/Button'
import Dropdown, { DropdownItem } from '../../components/Dropdown'
import Close from '../../icons/Close'
import Copy from '../../icons/Copy'
import Dots from '../../icons/Dots'
import { Container } from '../../components/Animations'
import JSONPretty from 'react-json-pretty'
import { StatusBadge } from '../CheckoutSessions'
import BreadCrumbs from '../../components/Breadcrumbs'
import Checkbox from '../../components/Checkbox'

interface ViewMessageForm {
  webhookMessage?: IWebhookMessage
}

const ViewWebhook = observer((): ReactElement => {
  const params = useParams()
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMessages, setIsLoadingMessages] = useState(true)

  const [webhook, setWebhook] = useState<IWebhook | undefined>(undefined)
  const [webhookMessageFilter, setWebhookMessageFilter] =
  useState<WebhookMessagesStatus[]>([...Object.values(WebhookMessagesStatus)])
  const [webhookMessages, setWebhookMessages] = useState<IWebhookMessage[]>([])
  const [webhookMessagesTotal, setWebhookMessagesTotal] = useState(0)
  const pageSize = 50
  const [curPage, setCurPage] = useState(0)
  const [viewMessageForm, updateViewMessageForm] = useReducer((p: ViewMessageForm, n: Partial<ViewMessageForm>) => {
    return { ...p, ...n }
  }, {
    webhookMessage: undefined
  })

  const loadWebhook = (id: string): void => {
    setIsLoading(true)
    Radom.getWebhook(id)
      .then(w => setWebhook(w))
      .catch(err => errorToast(err.message))
      .finally(() => setIsLoading(false))
  }

  const loadWebhookMessages = (webhookId: string, page: number, filter: WebhookMessagesStatus[]): void => {
    setIsLoadingMessages(true)
    setWebhookMessages([])
    setWebhookMessagesTotal(0)
    const filterKeys = filter.map(status => {
      return Object.keys(WebhookMessagesStatus).find(
        key => WebhookMessagesStatus[key as keyof typeof WebhookMessagesStatus] === status
      ) || status
    })
    Radom.getWebhookMessages(webhookId, pageSize, page * pageSize, filterKeys as any)
      .then(wfs => {
        setWebhookMessages(wfs.data)
        setWebhookMessagesTotal(wfs.total)
        setCurPage(page)
      })
      .catch(err => errorToast(err.message))
      .finally(() => setIsLoadingMessages(false))
  }

  const debouncedLoadWebhookMessages = useMemo(() => _.debounce(loadWebhookMessages, 600), [])

  useEffect(() => {
    if (params.webhookId) {
      loadWebhook(params.webhookId)
    }
  }, [])

  useEffect(() => {
    if (params.webhookId) {
      debouncedLoadWebhookMessages(params.webhookId, 0, webhookMessageFilter)
    }
    return () => debouncedLoadWebhookMessages.cancel()
  }, [webhookMessageFilter, debouncedLoadWebhookMessages])

  const webhookMessageRows = (): IWebhookMessage[] => {
    return webhookMessages
      .slice()
      .sort((a, b) => {
        const ad = a.nextDeliveryAttemptTimestamp ?? a.createdAt
        const bd = b.nextDeliveryAttemptTimestamp ?? b.createdAt
        return new Date(bd).getTime() - new Date(ad).getTime()
      })
  }

  const onPageClick = (i: number): void => {
    if (webhook) {
      setWebhookMessages([])
      loadWebhookMessages(webhook.id, i, webhookMessageFilter)
    }
  }

  if (isLoading) {
    return <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      paddingTop: 140,
      gap: 20
    }}>
      <Spinner />
    </div>
  }

  if (webhook) {
    return <Container>

      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <BreadCrumbs items={[
          { to: '/developers/webhooks', item: 'Webhooks' },
          { to: '.', item: webhook.id }
        ]} />

        <div style={{ display: 'flex', gap: 10 }}>

          <Dropdown
            selectedContent={<div style={{ fontSize: 14 }}>Message status</div>}
            dropdownContent={<div style={{ position: 'relative' }}>
              {isLoadingMessages && <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(255, 255, 255, 0.6)',
                  zIndex: 10
                }}
              >
                <Spinner />
              </div>}
              {Object.values(WebhookMessagesStatus).map(s =>
                <DropdownItem
                  key={s}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: 14 }}
                  onClick={() => {
                    if (isLoadingMessages) return
                    if (webhookMessageFilter.length === 1 && webhookMessageFilter.includes(s)) {
                      return
                    }
                    setWebhookMessageFilter((prev) =>
                      prev.includes(s)
                        ? prev.filter((x) => x !== s)
                        : [...prev, s])
                  }}>
                  <Checkbox size={15} checked={webhookMessageFilter.includes(s)} />
                  <span>{s}</span>
                </DropdownItem>
              )}
            </div>} />

        </div>

        <Table
          borderless
          isLoading={isLoadingMessages}
          emptyPlaceholderText={curPage === 0 ? 'No webhook messages' : 'No more webhook messages'}
          headers={[
            <span key="id">Message</span>,
            <span key="status">Status</span>,
            <span key="res">Response code</span>,
            <span key="retryTime">Last sent at</span>,
            <span key="retries">Retries</span>,
            <span key="createdTime">Created at</span>,
            <span key="action"></span>
          ]}
          items={
            webhookMessageRows().map(message => [
              <div key="id" style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <div>
                  {message.messagePayload.eventType}
                </div>
                <span style={{ opacity: 0.5, fontSize: 12 }}>{message.id}</span>
              </div>,
              <div key="status" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <StatusBadge status={message.messageStatus.toLowerCase() as any}>
                  {message.messageStatus}
                </StatusBadge>
              </div>,
              <div key="responseCode">
                {message.deliveryResponseStatusCode ? <span>{message.deliveryResponseStatusCode}</span> : '-'}
              </div>,
              <TimeText key="retryTime" date={new Date(message.nextDeliveryAttemptTimestamp)} />,
              <div key="retries">{message.numFailedDeliveryAttempts}</div>,
              <TimeText key="createdTime" date={new Date(message.createdAt)} />,
              <div key="action" onClick={e => e.stopPropagation()} style={{ display: 'flex' }}>
                {
                  <Dropdown
                    noChevron
                    selectedContent={<Dots width={15} />}
                    dropdownContent={
                      <div style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                        <DropdownItem
                          onClick={() => {
                            updateViewMessageForm({ webhookMessage: message })
                          }}>View
                        </DropdownItem>
                        <ConfirmButtonModal
                          title='Resend webhook message'
                          description='Resend webhook message.'
                          action='Resend webhook message'
                          onConfirm={async () => {
                            await Radom.resendWebhookMessage(message.webhookId, message.id)
                              .then(() => {
                                successToast('Resending webhook message')
                                loadWebhookMessages(message.webhookId, curPage, webhookMessageFilter)
                              })
                              .catch(err => {
                                errorToast(err.response?.data?.error || err.message)
                              })
                          }}
                          ActionComponent={props => <DropdownItem {...props}>Resend message</DropdownItem>}
                        />
                      </div>
                    }
                  />
                }
              </div>
            ])
          }
        />

        <Pagination
          curPage={curPage}
          numPages={Math.ceil(webhookMessagesTotal / pageSize)}
          onPageClick={onPageClick}
        />

        <Modal
          visible={viewMessageForm.webhookMessage !== undefined}
          onClose={() => updateViewMessageForm({ webhookMessage: undefined })}
          innerStyles={{ padding: 40, minHeight: 'unset' }}>
          {
            viewMessageForm.webhookMessage &&
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <PageHeader title="Webhook message" subTitle={`${viewMessageForm.webhookMessage.id}`} />
                <IconButton type="button" onClick={() => updateViewMessageForm({ webhookMessage: undefined })}>
                  <Close style={{ width: 15, height: 15 }} />
                </IconButton>
              </div>
              <div style={{
                display: 'flex',
                border: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
                backgroundColor: RADOM_COLORS.GRAY_LIGHTEST,
                justifyContent: 'space-between',
                fontSize: 14,
                position: 'relative',
                padding: '10px 20px',
                borderRadius: 5
              }}>
                <JSONPretty
                  data={viewMessageForm.webhookMessage.messagePayload}
                  theme={{
                    main: 'color:gray;overflow:auto;',
                    key: 'color:#f92672;',
                    string: 'color:black;',
                    value: 'color:#a6e22e;',
                    boolean: 'color:#ac81fe;'
                  }}
                  style={{ maxWidth: '100%', maxHeight: '50vh', overflow: 'scroll' }} />

                <IconButton style={{ padding: 10, position: 'absolute', top: 10, right: 10 }}>
                  <Copy style={{ width: 20 }} onClick={() => {
                    if (viewMessageForm.webhookMessage) {
                      navigator.clipboard.writeText(
                        JSON.stringify(viewMessageForm.webhookMessage.messagePayload, null, 2)
                      )
                      infoToast('Message payload copied to clipboard')
                    }
                  }} />
                </IconButton>
              </div>
            </div>
          }
        </Modal>
      </div>

    </Container>
  }

  return <div style={{ fontSize: 14, marginTop: 140, maxWidth: 'max-content', margin: 'auto' }}>
    <span>Webhook not found</span>
  </div>
})

export default ViewWebhook
